import { useEffect, useRef, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useEditorStore } from "@/store/editorStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Type,
  Square,
  Circle,
  MousePointer,
  Upload as UploadIcon,
  Folder,
  Image as ImageIcon,
  Video as VideoIcon,
  CheckCircle2,
  Loader2,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const TEMPLATE_THUMBS = [
  { id: "t1", url: "https://images.unsplash.com/photo-1688141585146-1fb4a1358c87?w=400" },
  { id: "t2", url: "https://images.pexels.com/photos/196645/pexels-photo-196645.jpeg?w=400" },
  { id: "t3", url: "https://images.pexels.com/photos/6672290/pexels-photo-6672290.jpeg?w=400" },
  { id: "t4", url: "https://images.unsplash.com/photo-1542744095-291d1f67b221?w=400" },
];

const BRAND_COLORS = ["#002FA7", "#FF4500", "#FF8C00", "#10B981", "#111827", "#6B7280", "#F9FAFB", "#FFFFFF"];

function authedUrl(path) {
  const token = localStorage.getItem("ch_token");
  return `${API_BASE.replace(/\/api$/, "")}${path}${path.includes("?") ? "&" : "?"}token=${encodeURIComponent(token || "")}`;
}

export default function DrawerPanel({ active }) {
  const addElement = useEditorStore((s) => s.addElement);
  const pages = useEditorStore((s) => s.pages);
  const activePageId = useEditorStore((s) => s.activePageId);
  const setActivePage = useEditorStore((s) => s.setActivePage);
  const nav = useNavigate();

  const targetPageId = activePageId || pages[0]?.id;

  if (active === "templates") {
    return (
      <Panel title="Templates">
        <p className="text-xs text-gray-500 mb-3 leading-relaxed">
          Click a template to insert its hero block on the current page.
        </p>
        <div className="grid grid-cols-2 gap-3">
          {TEMPLATE_THUMBS.map((t) => (
            <button
              key={t.id}
              className="aspect-[4/5] rounded-md overflow-hidden ring-1 ring-gray-200 hover:ring-2 hover:ring-[#002FA7] transition-all group"
              onClick={() => {
                addElement(targetPageId, {
                  type: "image",
                  x: 80,
                  y: 80,
                  w: 400,
                  h: 240,
                  src: t.url,
                  fit: "cover",
                });
                addElement(targetPageId, {
                  type: "text",
                  x: 80,
                  y: 340,
                  w: 480,
                  h: 60,
                  text: "Big bold headline",
                  fontSize: 36,
                  fontWeight: 800,
                  fontFamily: "Cabinet Grotesk, sans-serif",
                  color: "#111827",
                });
                toast.success("Template block added");
              }}
              data-testid={`template-${t.id}`}
            >
              <img src={t.url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
            </button>
          ))}
        </div>
      </Panel>
    );
  }

  if (active === "elements") {
    return (
      <Panel title="Elements">
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-gray-500 mb-2">Shapes</p>
        <div className="grid grid-cols-4 gap-2 mb-5">
          <ShapeBtn onClick={() => addElement(targetPageId, { type: "shape", shape: "rect", x: 100, y: 100, w: 200, h: 120, fill: "#002FA7", radius: 8 })} testId="elem-rect">
            <Square className="w-6 h-6" />
          </ShapeBtn>
          <ShapeBtn onClick={() => addElement(targetPageId, { type: "shape", shape: "circle", x: 120, y: 120, w: 120, h: 120, fill: "#FF4500" })} testId="elem-circle">
            <Circle className="w-6 h-6" />
          </ShapeBtn>
          <ShapeBtn onClick={() => addElement(targetPageId, { type: "shape", shape: "rect", x: 140, y: 140, w: 200, h: 8, fill: "#111827", radius: 4 })} testId="elem-line">
            <div className="w-7 h-1 bg-gray-700 rounded" />
          </ShapeBtn>
          <ShapeBtn onClick={() => addElement(targetPageId, { type: "button", x: 160, y: 160, w: 180, h: 48, text: "Click me", fill: "#002FA7", color: "#fff" })} testId="elem-button">
            <MousePointer className="w-6 h-6" />
          </ShapeBtn>
        </div>
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-gray-500 mb-2">Stock images</p>
        <div className="grid grid-cols-2 gap-2">
          {TEMPLATE_THUMBS.map((t) => (
            <button
              key={t.id}
              className="aspect-square rounded overflow-hidden ring-1 ring-gray-200 hover:ring-2 hover:ring-[#002FA7]"
              onClick={() => addElement(targetPageId, { type: "image", x: 100, y: 100, w: 280, h: 180, src: t.url, fit: "cover" })}
              data-testid={`stock-image-${t.id}`}
            >
              <img src={t.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </Panel>
    );
  }

  if (active === "text") {
    return (
      <Panel title="Text">
        <p className="text-xs text-gray-500 mb-4">Click to drop a text block on your active page.</p>
        <div className="space-y-2">
          <TextBtn
            label="Add a heading"
            sub="Cabinet Grotesk · 48 / 800"
            onClick={() =>
              addElement(targetPageId, {
                type: "text",
                x: 80,
                y: 80,
                w: 520,
                h: 64,
                text: "Add a heading",
                fontSize: 48,
                fontWeight: 800,
                fontFamily: "Cabinet Grotesk, sans-serif",
                color: "#111827",
              })
            }
            testId="text-heading"
          />
          <TextBtn
            label="Add a subheading"
            sub="IBM Plex Sans · 24 / 600"
            onClick={() =>
              addElement(targetPageId, {
                type: "text",
                x: 80,
                y: 80,
                w: 480,
                h: 40,
                text: "Add a subheading",
                fontSize: 24,
                fontWeight: 600,
                color: "#374151",
              })
            }
            testId="text-subheading"
          />
          <TextBtn
            label="Add body text"
            sub="IBM Plex Sans · 16 / 400"
            onClick={() =>
              addElement(targetPageId, {
                type: "text",
                x: 80,
                y: 80,
                w: 460,
                h: 80,
                text: "Add a little bit of body text here. Edit me — make this story your own.",
                fontSize: 16,
                fontWeight: 400,
                color: "#4B5563",
              })
            }
            testId="text-body"
          />
        </div>
      </Panel>
    );
  }

  if (active === "brand") {
    return (
      <Panel title="Brand kit">
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-gray-500 mb-2">Brand colors</p>
        <div className="grid grid-cols-4 gap-2 mb-5">
          {BRAND_COLORS.map((c) => (
            <button
              key={c}
              className="aspect-square rounded-md ring-1 ring-gray-200 hover:ring-2 hover:ring-[#002FA7]"
              style={{ background: c }}
              onClick={() =>
                addElement(targetPageId, {
                  type: "shape",
                  shape: "rect",
                  x: 100,
                  y: 100,
                  w: 160,
                  h: 160,
                  fill: c,
                  radius: 8,
                })
              }
              data-testid={`brand-color-${c.replace("#", "")}`}
              title={c}
            />
          ))}
        </div>
        <p className="text-[10px] font-bold tracking-[0.1em] uppercase text-gray-500 mb-2">Fonts</p>
        <div className="space-y-2 text-sm">
          <div className="border border-gray-200 rounded-md p-3 font-display font-bold text-xl">Cabinet Grotesk</div>
          <div className="border border-gray-200 rounded-md p-3">IBM Plex Sans</div>
        </div>
      </Panel>
    );
  }

  if (active === "uploads") {
    return <UploadsPanel addElement={addElement} pageId={targetPageId} />;
  }

  if (active === "tools") {
    return (
      <Panel title="Tools">
        <ul className="space-y-2 text-sm text-gray-700">
          <li className="border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer">Spell-check</li>
          <li className="border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer">Color picker</li>
          <li className="border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer">Element tidy</li>
          <li className="border border-gray-200 rounded-md p-3 hover:bg-gray-50 cursor-pointer">Layer inspector</li>
        </ul>
      </Panel>
    );
  }

  if (active === "projects") {
    return (
      <Panel title="Projects">
        <Button
          className="w-full bg-[#002FA7] hover:bg-blue-800 text-white"
          onClick={() => nav("/dashboard")}
          data-testid="drawer-back-to-dashboard"
        >
          <Folder className="w-4 h-4 mr-1" /> Open dashboard
        </Button>
        <p className="text-xs text-gray-500 mt-3">
          Switch between all your projects from the dashboard.</p>
      </Panel>
    );
  }

  if (active === "apps") {
    return (
      <Panel title="Apps">
        <div className="grid grid-cols-2 gap-2 text-xs">
          {["Unsplash", "GIPHY", "Forms", "Emoji", "Charts", "Maps"].map((a) => (
            <div key={a} className="aspect-square rounded-md border border-gray-200 flex flex-col items-center justify-center hover:bg-gray-50 cursor-pointer">
              <Sparkles className="w-5 h-5 text-[#002FA7] mb-1" />
              {a}
            </div>
          ))}
        </div>
      </Panel>
    );
  }
  return null;
}

function Panel({ title, children }) {
  return (
    <div className="flex flex-col h-full ch-fade" data-testid={`drawer-panel-${title.toLowerCase().replace(/ /g, "-")}`}>
      <div className="px-4 py-3 border-b border-gray-200">
        <h2 className="font-display text-lg font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="flex-1 overflow-y-auto ch-scroll p-4">{children}</div>
    </div>
  );
}

function ShapeBtn({ children, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="aspect-square rounded-md border border-gray-200 flex items-center justify-center hover:bg-gray-50 hover:border-[#002FA7] transition-colors text-gray-700"
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function TextBtn({ label, sub, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left border border-gray-200 rounded-md p-3 hover:border-[#002FA7] hover:bg-blue-50/30 transition-colors"
      data-testid={testId}
    >
      <div className="font-display font-semibold tracking-tight">{label}</div>
      <div className="text-xs text-gray-500 mt-0.5">{sub}</div>
    </button>
  );
}

function UploadsPanel({ addElement, pageId }) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ percent: 0, total_size: 0, quota: 5 * 1024 * 1024 * 1024 });
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);
  const [filter, setFilter] = useState("images"); // images | videos | folders
  const fileRef = useRef();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/uploads");
      setItems(data.items);
      setStats({ percent: data.percent, total_size: data.total_size, quota: data.quota });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    const tempId = `temp-${Date.now()}`;
    setUploadingId(tempId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Upload complete");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingId(null);
    }
  };

  const handleDragStart = (e, item) => {
    e.dataTransfer.setData(
      "application/ch-image",
      JSON.stringify({ url: authedUrl(item.url), id: item.id }),
    );
    e.dataTransfer.effectAllowed = "copy";
  };

  const visible = items.filter((i) => (filter === "videos" ? i.kind === "video" : i.kind === "image"));

  const human = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)}MB` : `${(b / 1024).toFixed(0)}KB`);

  return (
    <Panel title="Uploads">
      {/* Storage bar */}
      <div className="mb-3" data-testid="storage-bar">
        <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
          <span>{stats.percent}% storage used</span>
          <span>{human(stats.total_size)} / 5 GB</span>
        </div>
        <div className="h-1.5 rounded-full bg-gray-200 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#002FA7] to-[#FF4500]"
            style={{ width: `${Math.min(100, stats.percent)}%` }}
          />
        </div>
      </div>

      <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={handleUpload} data-testid="upload-file-input" />
      <div className="flex gap-2 mb-3">
        <Button
          className="flex-1 bg-[#002FA7] hover:bg-blue-800 text-white"
          onClick={() => fileRef.current?.click()}
          data-testid="upload-files-button"
        >
          <UploadIcon className="w-4 h-4 mr-1" /> Upload files
        </Button>
        <Button variant="outline" className="px-3" disabled title="Record yourself (coming soon)">
          <VideoIcon className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex gap-1 mb-3 text-xs font-medium" data-testid="uploads-filters">
        {["images", "videos", "folders"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`px-3 py-1.5 rounded-full ${
              filter === k ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
            data-testid={`uploads-filter-${k}`}
          >
            {k[0].toUpperCase() + k.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center text-sm text-gray-500 py-10 border border-dashed border-gray-300 rounded-md">
          <ImageIcon className="w-6 h-6 mx-auto mb-2 text-gray-400" />
          No {filter} yet.<br />
          <span className="text-xs">Click &quot;Upload files&quot; to add some.</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2" data-testid="uploads-grid">
          {uploadingId && (
            <div className="aspect-square rounded-md bg-gray-100 flex items-center justify-center">
              <Loader2 className="w-5 h-5 animate-spin text-gray-500" />
            </div>
          )}
          {visible.map((it) => (
            <div
              key={it.id}
              className="aspect-square rounded-md overflow-hidden ring-1 ring-gray-200 hover:ring-2 hover:ring-[#002FA7] relative group cursor-grab"
              draggable
              onDragStart={(e) => handleDragStart(e, it)}
              onClick={() =>
                addElement(pageId, {
                  type: "image",
                  x: 100,
                  y: 100,
                  w: 300,
                  h: 200,
                  src: authedUrl(it.url),
                  fit: "cover",
                })
              }
              data-testid={`upload-item-${it.id}`}
            >
              {it.kind === "video" ? (
                <video src={authedUrl(it.url)} className="w-full h-full object-cover" />
              ) : (
                <img src={authedUrl(it.url)} alt="" className="w-full h-full object-cover" draggable={false} />
              )}
              <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-bold uppercase tracking-wider flex items-center gap-0.5">
                <CheckCircle2 className="w-3 h-3" /> Complete
              </div>
              <button
                className="absolute bottom-1 right-1 w-6 h-6 bg-white/90 rounded flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!window.confirm("Delete this upload?")) return;
                  await api.delete(`/uploads/${it.id}`);
                  load();
                }}
                data-testid={`upload-delete-${it.id}`}
              >
                <Trash2 className="w-3 h-3 text-red-600" />
              </button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

export { authedUrl };
